import { Candle, SearchParams } from '../types';

// 缓存系统
interface CacheItem {
  data: Candle[];
  timestamp: number;
}

// 数据缓存
const dataCache: Record<string, CacheItem> = {};
const CACHE_TTL = 5 * 60 * 1000; // 缓存有效期5分钟

// 时间周期映射
const TIMEFRAME_MAP: Record<string, string> = {
  '1m': '1m',    // 1分钟
  '3m': '3m',    // 3分钟
  '5m': '5m',    // 5分钟
  '15m': '15m',  // 15分钟
  '30m': '30m',  // 30分钟
  '1h': '1h',    // 1小时
  '2h': '2h',    // 2小时
  '4h': '4h',    // 4小时
  '6h': '6h',    // 6小时
  '12h': '12h',  // 12小时
  '1d': '1d',    // 1天
  '1w': '1w',    // 1周
  '1M': '1M',    // 1月
};

// OKX API 时间周期映射
const OKX_TIMEFRAME_MAP: Record<string, string> = {
  '1m': '1m',    // 1分钟
  '3m': '3m',    // 3分钟
  '5m': '5m',    // 5分钟
  '15m': '15m',  // 15分钟
  '30m': '30m',  // 30分钟
  '1h': '1H',    // 1小时
  '2h': '2H',    // 2小时
  '4h': '4H',    // 4小时
  '6h': '6H',    // 6小时
  '12h': '12H',  // 12小时
  '1d': '1D',    // 1天
  '1w': '1W',    // 1周
  '1M': '1M',    // 1月
};

// 预定义的时间周期
const DEFAULT_TIMEFRAMES = Object.keys(TIMEFRAME_MAP);

// OKX API URL
const OKX_API_URL = 'https://www.okx.com';

/**
 * OKX API 客户端
 * 使用浏览器原生 fetch API 实现
 */
class OKXClient {
  /**
   * 发送 API 请求
   */
  async sendRequest(endpoint: string, params: Record<string, any> = {}): Promise<any> {
    // 构建查询字符串
    const queryString = Object.entries(params)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
      .join('&');
    
    // 构建完整 URL
    const url = `${OKX_API_URL}${endpoint}${queryString ? '?' + queryString : ''}`;
    
    try {
      console.log(`发送请求到: ${url}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('OKX API 请求失败:', error);
      throw error;
    }
  }
  
  /**
   * 获取所有交易对
   */
  async getInstruments(instType: string = 'SPOT'): Promise<any> {
    return this.sendRequest('/api/v5/public/instruments', { instType });
  }
  
  /**
   * 获取行情数据
   */
  async getTickers(instType: string = 'SPOT'): Promise<any> {
    return this.sendRequest('/api/v5/market/tickers', { instType });
  }
  
  /**
   * 获取 K 线数据
   */
  async getCandles(instId: string, bar: string, limit: number = 100): Promise<any> {
    return this.sendRequest('/api/v5/market/candles', { instId, bar, limit });
  }
}

// 创建 OKX 客户端实例
const okxClient = new OKXClient();

/**
 * 获取常用交易对列表
 * @returns 常用交易对数组
 */
export const fetchSymbols = async (): Promise<string[]> => {
  try {
    // 获取交易对行情数据
    const response = await okxClient.getTickers();
    
    if (!response || !response.data || !Array.isArray(response.data)) {
      throw new Error('获取交易对列表失败: 无效的响应数据');
    }
    
    // 过滤并按成交量排序
    const symbols = response.data
      .filter((ticker: any) => ticker.instId && ticker.instId.endsWith('-USDT'))
      .sort((a: any, b: any) => parseFloat(b.vol24h || '0') - parseFloat(a.vol24h || '0'))
      .slice(0, 30) // 增加到30个热门交易对
      .map((ticker: any) => {
        // 转换格式 BTC-USDT -> BTC/USDT
        const instId = ticker.instId;
        return instId.replace('-', '/');
      });
    
    return symbols;
  } catch (error) {
    console.error('获取交易对列表失败:', error);
    // 如果 API 请求失败，返回更多默认交易对
    return [
      'BTC/USDT',
      'ETH/USDT',
      'OKB/USDT',
      'SOL/USDT', // 确保包含SOL
      'XRP/USDT',
      'DOGE/USDT',
      'ADA/USDT',
      'AVAX/USDT',
      'DOT/USDT',
      'SHIB/USDT',
      'LTC/USDT',
      'LINK/USDT',
      'MATIC/USDT',
      'UNI/USDT',
      'ATOM/USDT'
    ];
  }
};

/**
 * 获取可用的时间周期
 * @returns 时间周期列表
 */
export const getAvailableTimeframes = (): string[] => {
  return DEFAULT_TIMEFRAMES;
};

/**
 * 将交易对转换为 OKX 格式
 * @param symbol 交易对，如 "BTC/USDT"
 * @returns OKX 格式的交易对，如 "BTC-USDT"
 */
const formatSymbol = (symbol: string): string => {
  return symbol.replace('/', '-');
};

/**
 * 获取 K 线数据
 * @param params 搜索参数
 * @returns K 线数据数组
 */
export const fetchCandleData = async (params: SearchParams): Promise<Candle[]> => {
  try {
    const { symbol, timeframe, limit = 100 } = params;
    
    // 生成缓存键
    const cacheKey = `${symbol}-${timeframe}-${limit}`;
    
    // 检查缓存是否有效
    const now = Date.now();
    if (dataCache[cacheKey] && now - dataCache[cacheKey].timestamp < CACHE_TTL) {
      console.log('使用缓存数据:', { symbol, timeframe, limit });
      return dataCache[cacheKey].data;
    }
    
    console.log('从 OKX 获取新数据:', { symbol, timeframe, limit });
    
    // 转换为 OKX 格式
    const formattedSymbol = formatSymbol(symbol);
    const okxTimeframe = OKX_TIMEFRAME_MAP[timeframe] || '1H';
    
    // 从 OKX 获取 K 线数据
    const response = await okxClient.getCandles(formattedSymbol, okxTimeframe, limit);
    
    if (!response || !response.data || !Array.isArray(response.data)) {
      throw new Error('获取 K 线数据失败: 无效的响应数据');
    }
    
    // 转换为我们的 Candle 格式
    // OKX K线数据格式: [时间戳(String), 开盘价, 最高价, 最低价, 收盘价, 成交量, 成交额]
    const candles: Candle[] = response.data.map((kline: any[]) => ({
      timestamp: Number(kline[0]), // 时间戳
      open: Number(kline[1]),      // 开盘价
      high: Number(kline[2]),      // 最高价
      low: Number(kline[3]),       // 最低价
      close: Number(kline[4]),     // 收盘价
      volume: Number(kline[5])     // 成交量
    })).reverse(); // OKX 返回的是最新到最旧，需要反转顺序
    
    // 保存到缓存
    dataCache[cacheKey] = {
      data: candles,
      timestamp: now
    };
    
    console.log(`成功获取 ${candles.length} 条 K 线数据`);
    return candles;
  } catch (error: any) {
    console.error('获取 K 线数据失败:', error);
    throw new Error(`获取数据失败: ${error.message}`);
  }
};

/**
 * 搜索交易对
 * @param query 搜索关键词
 * @returns 匹配的交易对列表
 */
export const searchSymbols = async (query: string): Promise<string[]> => {
  // 主流币种列表，确保这些始终可用
  const popularTokens = [
    'BTC/USDT', 'ETH/USDT', 'SOL/USDT', 'XRP/USDT', 'DOGE/USDT',
    'ADA/USDT', 'AVAX/USDT', 'DOT/USDT', 'SHIB/USDT', 'LTC/USDT',
    'LINK/USDT', 'MATIC/USDT', 'UNI/USDT', 'ATOM/USDT', 'ZEN/USDT'
  ];

  try {
    if (!query) {
      return popularTokens; // 空查询时返回热门代币
    }
    
    const queryUpper = query.toUpperCase();
    
    // 先检查主流币种是否匹配查询
    const popularMatches = popularTokens.filter(token => 
      token.includes(queryUpper)
    );
    
    // 如果是直接搜索特定热门币种，立即返回
    if (queryUpper === 'SOL' || queryUpper === 'BTC' || queryUpper === 'ETH' || queryUpper === 'ZEN') {
      const exactMatch = popularTokens.filter(token => 
        token.startsWith(queryUpper + '/')
      );
      if (exactMatch.length > 0) {
        return exactMatch;
      }
    }
    
    // 直接使用OKX的全部交易对API，不进行过滤，而是在前端做搜索
    const response = await okxClient.getInstruments('SPOT');
    
    if (!response || !response.data || !Array.isArray(response.data)) {
      console.warn('无法获取交易对信息，使用预定义列表');
      return popularMatches.length > 0 ? popularMatches : popularTokens;
    }
    
    // 过滤 USDT 交易对并匹配搜索关键词
    // 1. 不区分大小写搜索
    // 2. 搜索范围包括交易对ID和基础货币
    // 3. 支持部分匹配
    const matchedSymbols = response.data
      .filter((instrument: any) => 
        instrument.instId && 
        instrument.instId.endsWith('-USDT') && 
        instrument.state === 'live'
      )
      .filter((instrument: any) => {
        // 更宽松的搜索条件
        const instId = instrument.instId.toUpperCase();
        const baseCcy = (instrument.baseCcy || '').toUpperCase();
        // 搜索交易对ID或基础货币
        return instId.includes(queryUpper) || baseCcy.includes(queryUpper);
      })
      .map((instrument: any) => {
        // 转换格式 BTC-USDT -> BTC/USDT
        return instrument.instId.replace('-', '/');
      })
      .slice(0, 50); // 最多50个结果，增加了匹配数量
    
    // 合并结果，确保热门币种始终在前面
    const combinedResults = [
      ...popularMatches,
      ...matchedSymbols.filter((s: string) => !popularMatches.includes(s))
    ];
    
    return combinedResults;
  } catch (error) {
    console.error('搜索交易对失败:', error);
    
    // 失败时，尝试匹配主流币种
    if (!query) return popularTokens;
    
    const queryUpper = query.toUpperCase();
    const matches = popularTokens.filter(token => 
      token.includes(queryUpper)
    );
    
    return matches.length > 0 ? matches : popularTokens;
  }
};

/**
 * WebSocket 处理类
 */
class OKXWebSocket {
  private ws: WebSocket | null = null;
  private callbacks: Map<string, (data: any) => void> = new Map();
  private pingInterval: any = null;
  
  /**
   * 连接 WebSocket
   */
  connect(): void {
    // 关闭已有连接
    if (this.ws) {
      this.disconnect();
    }
    
    // 创建新连接
    this.ws = new WebSocket('wss://ws.okx.com:8443/ws/v5/public');
    
    // 连接打开时的处理
    this.ws.onopen = () => {
      console.log('WebSocket 连接已建立');
      
      // 开始发送 ping 消息保持连接
      this.pingInterval = setInterval(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ op: 'ping' }));
        }
      }, 15000);
    };
    
    // 接收消息的处理
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        
        // 处理 pong 响应
        if (data.event === 'pong') {
          return;
        }
        
        // 处理数据更新
        if (data.data && Array.isArray(data.data)) {
          // 找到匹配的回调函数
          this.callbacks.forEach((callback, key) => {
            if (data.arg && data.arg.channel === 'candle' + key) {
              callback(data);
            }
          });
        }
      } catch (error) {
        console.error('解析 WebSocket 消息失败:', error);
      }
    };
    
    // 连接关闭时的处理
    this.ws.onclose = () => {
      console.log('WebSocket 连接已关闭');
      this.clearPingInterval();
      this.ws = null;
    };
    
    // 错误处理
    this.ws.onerror = (error) => {
      console.error('WebSocket 错误:', error);
      this.disconnect();
    };
  }
  
  /**
   * 订阅频道
   */
  subscribe(instId: string, channel: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.connect();
      
      // 等待连接建立后发送订阅请求
      this.ws!.onopen = () => {
        this.sendSubscribeRequest(instId, channel);
      };
    } else {
      this.sendSubscribeRequest(instId, channel);
    }
  }
  
  /**
   * 发送订阅请求
   */
  private sendSubscribeRequest(instId: string, channel: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      const subscribeMsg = {
        op: 'subscribe',
        args: [
          {
            channel: 'candle' + channel,
            instId: instId
          }
        ]
      };
      
      this.ws.send(JSON.stringify(subscribeMsg));
      console.log(`已发送订阅请求: ${JSON.stringify(subscribeMsg)}`);
    }
  }
  
  /**
   * 添加回调函数
   */
  addCallback(channel: string, callback: (data: any) => void): void {
    this.callbacks.set(channel, callback);
  }
  
  /**
   * 清除 ping 间隔
   */
  private clearPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
  
  /**
   * 断开连接
   */
  disconnect(): void {
    this.clearPingInterval();
    
    if (this.ws) {
      // 发送取消订阅请求
      if (this.ws.readyState === WebSocket.OPEN) {
        const unsubscribeMsg = {
          op: 'unsubscribe',
          args: Array.from(this.callbacks.keys()).map(channel => ({
            channel: 'candle' + channel,
            instId: activeSymbol ? formatSymbol(activeSymbol) : 'BTC-USDT'
          }))
        };
        
        if (unsubscribeMsg.args.length > 0) {
          this.ws.send(JSON.stringify(unsubscribeMsg));
        }
      }
      
      this.ws.close();
      this.ws = null;
    }
    
    this.callbacks.clear();
  }
}

// 创建 WebSocket 实例
const websocketClient = new OKXWebSocket();

// 活跃的订阅
let activeSymbol: string | null = null;
let activeTimeframe: string | null = null;

/**
 * 订阅实时 K 线更新
 * @param symbol 交易对
 * @param timeframe 时间周期
 * @param onUpdate 数据更新回调函数
 */
export const subscribeToRealtimeUpdates = (
  symbol: string,
  timeframe: string,
  onUpdate: (candle: Candle) => void
): void => {
  // 如果已有连接且参数相同，不重复连接
  if (
    activeSymbol === symbol && 
    activeTimeframe === timeframe
  ) {
    return;
  }
  
  // 如果已有连接但参数不同，先断开
  if (activeSymbol || activeTimeframe) {
    unsubscribeFromRealtimeUpdates();
  }
  
  // 保存当前参数
  activeSymbol = symbol;
  activeTimeframe = timeframe;
  
  // 转换为 OKX 格式
  const formattedSymbol = formatSymbol(symbol);
  const okxTimeframe = OKX_TIMEFRAME_MAP[timeframe] || '1H';
  
  // 添加回调
  websocketClient.addCallback(okxTimeframe, (data) => {
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      // OKX WebSocket 返回的 K 线数据
      const klineData = data.data[0];
      
      // 转换为我们的 Candle 格式
      const candle: Candle = {
        timestamp: Number(klineData[0]), // 时间戳
        open: Number(klineData[1]),      // 开盘价
        high: Number(klineData[2]),      // 最高价
        low: Number(klineData[3]),       // 最低价
        close: Number(klineData[4]),     // 收盘价
        volume: Number(klineData[5])     // 成交量
      };
      
      // 调用回调函数更新 UI
      onUpdate(candle);
    }
  });
  
  // 订阅 K 线更新
  websocketClient.subscribe(formattedSymbol, okxTimeframe);
  
  console.log(`已订阅 ${symbol} ${timeframe} 的实时 K 线更新`);
};

/**
 * 取消实时 K 线更新订阅
 */
export const unsubscribeFromRealtimeUpdates = (): void => {
  websocketClient.disconnect();
  activeSymbol = null;
  activeTimeframe = null;
  console.log('已取消 K 线更新订阅');
};